// Styles
import styled from 'styled-components/macro'
import colors from 'styles/colors'

// Core
import React, {ChangeEventHandler} from 'react'

// Components, services, etc
import TextInput from 'components/TextInput'
import {EDIT} from 'constants/paymentMethodConstants'

// 3rd-party
import {CardCvcElement, CardExpiryElement, CardNumberElement} from '@stripe/react-stripe-js'
import {Field, FieldInputProps} from 'react-final-form'
import type {GetUserWalletResponse} from 'types/orderTypes'

type Props = {
  mode: 'PAY' | 'EDIT'
  wallet: GetUserWalletResponse
}

type CardSectionStripeElementProps = {
  inputRef: (a?: Node | null) => void
  Component: typeof CardNumberElement | typeof CardExpiryElement | typeof CardCvcElement
  options: {}
}

const stripeStyles = {
  base: {
    color: colors.grayscale.black,
    fontWeight: 400,
    fontFamily: 'Inter, Roboto, Arial, Times New Roman, sans-serif',
    fontSize: '14px',
    fontSmoothing: 'antialiased',
    '::placeholder': {
      color: colors.stone.base
    },
    width: '100%',
    background: colors.grayscale.white,
    border: `1px solid ${colors.stone.light}`,
    boxShadow: 'inset 1px 1px 3px rgba(0, 0, 0, 0.1)',
    borderRadius: '6px'
  },
  invalid: {
    fontWeight: 400,
    iconColor: colors.ruby.base,
    color: colors.grayscale.black
  }
}

const CardSection = ({mode, wallet}: Props) => {
  const {stripeCustomerHasSavedPaymentMethod} = wallet
  const isEditMode = mode === EDIT && stripeCustomerHasSavedPaymentMethod
  const handleInputChange: (
    input: FieldInputProps<any, HTMLElement>
  ) => ChangeEventHandler<HTMLInputElement> = input => valueObject => {
    input.onChange(valueObject)
  }

  return (
    <CardSection.Styled>
      <div className='row'>
        <Field name='name'>
          {({input, meta}) => {
            return (
              <TextInput
                placeholder='Name on Card'
                label='Card Details'
                value={input.value}
                onChange={handleInputChange(input)}
                error={meta.error && meta.touched}
                helperText={meta.touched && meta.error}
                disabled={isEditMode}
              />
            )
          }}
        </Field>
      </div>
      <div className='row'>
        {isEditMode ? (
          <CardSection.CardNumberDisplayOnly wallet={wallet} />
        ) : (
          <Field name='cardNumber'>
            {({input, meta}) => {
              return (
                <TextInput
                  onChange={handleInputChange(input)}
                  error={meta.error && meta.touched}
                  helperText={meta.touched && meta.error}
                  InputProps={{
                    inputComponent: CardSection.StripeElement,
                    inputProps: {
                      Component: CardNumberElement,
                      options: {
                        showIcon: true,
                        disabled: isEditMode,
                        value: input.value
                      }
                    }
                  }}
                  disabled={isEditMode}
                />
              )
            }}
          </Field>
        )}
      </div>
      {!isEditMode && (
        <div className='row split'>
          <Field name='cardExpiry'>
            {({input, meta}) => {
              return (
                <TextInput
                  onChange={handleInputChange(input)}
                  error={meta.error && meta.touched}
                  helperText={meta.touched && meta.error}
                  InputProps={{
                    inputComponent: CardSection.StripeElement,
                    inputProps: {
                      Component: CardExpiryElement
                    }
                  }}
                />
              )
            }}
          </Field>
          <Field name='cardCvc'>
            {({input, meta}) => {
              return (
                <TextInput
                  onChange={handleInputChange(input)}
                  error={meta.error && meta.touched}
                  helperText={meta.touched && meta.error}
                  InputProps={{
                    inputComponent: CardSection.StripeElement,
                    inputProps: {
                      Component: CardCvcElement
                    }
                  }}
                />
              )
            }}
          </Field>
        </div>
      )}
    </CardSection.Styled>
  )
}

CardSection.CardNumberDisplayOnly = ({wallet: {last4}}: {wallet: Props['wallet']}) => {
  return <TextInput value={`•••• •••• •••• ${last4}`} disabled />
}

CardSection.StripeElement = (props: CardSectionStripeElementProps) => {
  const {inputRef, Component, options, ...rest} = props
  const optionsWithStyles: any = {
    ...options,
    style: stripeStyles
  }
  return (
    <div
      className='stripe-element-wrapper'
      ref={ref => {
        // we need to access the internal input element that Stripe creates
        inputRef(ref ? ref.querySelector('input') : null)
      }}
    >
      <Component options={optionsWithStyles} {...rest} />
    </div>
  )
}

CardSection.Styled = styled.section`
  .stripe-element-wrapper {
    width: 100%;
  }

  .StripeElement {
    box-sizing: border-box;
    padding: 10px 12px;
  }
`

export default CardSection
